/**
 * ClockBar (PR-10, PR-50, DES-04).
 *
 * Horizontal progress track for an active task. Fill width = elapsed /
 * cycle_duration. Color transitions on the per-tier thresholds from
 * @studio/design-tokens CLOCK_BAR_THRESHOLDS (Studio 0-48 green / 48-60 amber
 * / 60-72 red; Pro 0-32 / 32-40 / 40-48). Pulsing dot at the leading edge
 * when status is in_progress. ETA = pull_time + cycle_duration (PR-50),
 * surfaced relative on the bar and absolute in the caller's metadata.
 *
 * Renders the same visual logic the operator dashboard uses (DES-04); this is
 * the subscriber-portal instance.
 */

import { CLOCK_BAR_THRESHOLDS } from '@studio/design-tokens';
import type { TaskStatus } from '@studio/api-schemas';

export function ClockBar({
  tier,
  pullTime,
  status,
}: {
  tier: 'studio' | 'pro';
  pullTime: string | null;
  status: TaskStatus;
}) {
  const t = CLOCK_BAR_THRESHOLDS[tier];
  if (!pullTime) {
    return (
      <p className="text-xs text-[color:var(--color-text-mute)]">
        The clock starts when this task is pulled.
      </p>
    );
  }

  const start = new Date(pullTime).getTime();
  const now = Date.now();
  const elapsedHours = Math.max(0, (now - start) / 3_600_000);
  const pct = Math.min(100, (elapsedHours / t.cycleHours) * 100);
  const remainingHours = Math.max(0, t.cycleHours - elapsedHours);

  // Green uses the brighter --color-green-accent to match the prototype's
  // "in progress" green signal; amber/red stay on the clock semantic aliases.
  const color =
    elapsedHours < t.greenUntilHours
      ? 'var(--color-green-accent)'
      : elapsedHours < t.amberUntilHours
        ? 'var(--color-clock-amber)'
        : 'var(--color-clock-red)';

  const eta = new Date(start + t.cycleHours * 3_600_000);
  const pulsing = status === 'in_progress';

  return (
    <div>
      <div
        className="relative h-2.5 w-full overflow-hidden rounded-full bg-[color:var(--color-linen)]"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={t.cycleHours}
        aria-valuenow={Math.round(elapsedHours)}
        aria-label="Delivery cycle progress"
      >
        <div
          className="h-full rounded-full transition-[width] duration-500"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
        {pulsing && pct < 100 ? (
          <span
            className="absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full motion-safe:animate-pulse"
            style={{ left: `calc(${pct}% - 5px)`, backgroundColor: color }}
            aria-hidden="true"
          />
        ) : null}
      </div>
      <p className="mt-1.5 text-xs text-[color:var(--color-text-mute)]">
        {remainingHours <= 0
          ? `Past the ${t.cycleHours}-hour mark`
          : `About ${Math.round(remainingHours)}h left of the ${t.cycleHours}-hour cycle`}{' '}
        · ETA {eta.toLocaleString('en-US')}
      </p>
    </div>
  );
}
